/**
 * Phase Utilities
 * 
 * Centralized phase management for the Heseda AI application.
 * 
 * Phase Definitions:
 * - Phase 1 (Profile Building): profile completion < 50%
 *   - Search NOT available
 *   - Sample request button visible
 *   - Focus on profile data collection
 * 
 * - Phase 2+ (Product Recommendations & beyond): profile completion >= 50%
 *   - Search IS available
 *   - Sample request button hidden (already qualified)
 *   - Focus on product matching and recommendations
 * 
 * Key Insight: Phase 1 is the ONLY phase with feature restrictions.
 * Once a user exits Phase 1, all features become available.
 * Future phases (Phase 3: Healthcare Manager, etc.) will ADD features, not restrict them.
 * 
 * @author Heseda AI Team
 * @version 1.0
 */

/** Minimum profile completion required to exit Phase 1 */
export const PHASE_1_THRESHOLD = 50;

/** Phase types */
export type Phase = 'phase1' | 'phase2' | 'phase3';

/**
 * Determines if user is in Phase 1 (profile building, search restricted)
 * Phase 1 ends when profile completion reaches PHASE_1_THRESHOLD
 * 
 * @param profileCompletion - User's profile completion percentage (0-100)
 * @returns true if user is in Phase 1 (search NOT available)
 * 
 * @example
 * ```ts
 * const isPhase1 = isInPhase1(45); // true - search blocked
 * const isPhase1 = isInPhase1(50); // false - search available
 * ```
 */
export function isInPhase1(profileCompletion: number): boolean {
  return profileCompletion < PHASE_1_THRESHOLD;
}

/**
 * Determines if search functionality is available
 * Search is available in all phases EXCEPT Phase 1
 * 
 * @param profileCompletion - User's profile completion percentage (0-100)
 * @returns true if search is available
 */
export function isSearchAvailable(profileCompletion: number): boolean {
  return !isInPhase1(profileCompletion);
}

/**
 * Gets the current phase based on profile completion
 * 
 * @param profileCompletion - User's profile completion percentage (0-100)
 * @returns Current phase identifier
 */
export function getCurrentPhase(profileCompletion: number): Phase {
  if (profileCompletion < PHASE_1_THRESHOLD) {
    return 'phase1';
  }
  // Future: Add Phase 3 logic when Healthcare Manager is implemented
  // if (profileCompletion >= PHASE_3_THRESHOLD && hasHealthData) return 'phase3';
  return 'phase2';
}

/**
 * Gets the phase name for display
 * 
 * @param phase - Phase identifier
 * @param language - Display language
 * @returns Localized phase name
 */
export function getPhaseName(phase: Phase, language: string): string {
  const names: Record<Phase, Record<string, string>> = {
    phase1: {
      en: 'Profile Consultant',
      ko: '프로필 상담사',
      'pt-BR': 'Consultor de Perfil',
      es: 'Consultor de Perfil',
    },
    phase2: {
      en: 'Product Expert', 
      ko: '제품 전문가',
      'pt-BR': 'Especialista em Produtos',
      es: 'Experto en Productos',
    },
    phase3: {
      en: 'Healthcare Manager',
      ko: '헬스케어 매니저',
      'pt-BR': 'Gerente de Saúde',
      es: 'Gestor de Salud',
    }
  };
  return names[phase][language] || names[phase]['en'];
}

/**
 * Gets the message to display when a Phase 1 user tries to use search
 * 
 * @param profileCompletion - Current profile completion percentage
 * @param language - Display language
 * @returns User-friendly message explaining search is not available
 */
export function getPhase1SearchBlockedMessage(
  profileCompletion: number,
  language: string
): string {
  const remaining = Math.max(0, PHASE_1_THRESHOLD - profileCompletion);
  
  return language === 'ko'
    ? `제품 검색은 프로필 완성도 ${PHASE_1_THRESHOLD}%에서 열려요. 지금은 ${profileCompletion}% 완성됐어요. ${remaining}%만 더 채우면 맞춤 추천을 받을 수 있어요!`
    : `Product search unlocks at ${PHASE_1_THRESHOLD}% profile completion. You're currently at ${profileCompletion}%. Just ${remaining}% more to get personalized recommendations!`;
}
